import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import useGetDropdownValues from '../CustomHooks/useGetDropdownValues';
import { Alert, Autocomplete } from '@mui/material';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import useGetAllWH from '../CustomHooks/useGetAllWH';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';
import { useGetCustomerWeighbridgeDetails } from 'services/globalHooks/useGetCustomerWeighbridgeDetails';

function camelCaseToLabel(text) {
  return text?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str.toUpperCase());
}

const EditSectionModal = ({ open, handleEditClose, sectionName, sectionData, handleEditSave, getInputArrayList }) => {
  const [formData, setFormData] = useState(sectionData);
  const [transferUsers, setTransferUsers] = useState([]);

  const {
    rows: cwRows,
    destinations,
    customers,
    weighbridges,
  } = useGetCustomerWeighbridgeDetails();
  const trucks = useGetDropdownValues('Truck');
  const trailers = useGetDropdownValues('Trailer');
  const loadMachine = useGetDropdownValues('LoaderMachineTruck');
  const { driverList: drivers } = useGetDropdownValues('driver');
  const { loaderList: loaders } = useGetDropdownValues('driver');
  const product = useGetDropdownValues('product');
  const allWH = useGetAllWH();
  const [pricingError, setPricingError] = useState('');
  const [isPricingValid, setIsPricingValid] = useState(true);

  const validateTimberPricing = useCallback(async data => {
    const { spNumber, name, timberProductUsedForWoodchip } = data;

    if (!spNumber || !name || !timberProductUsedForWoodchip) {
      setPricingError('');
      setIsPricingValid(true);
      return;
    }

    try {
      const response = await customAxios.get(`/Ops/GetContractProductDestinations/${spNumber}`, { headers: authHeader() });

      console.log('response ===', response);

      // Safely ensure we are working with an array
      const responseData = Array.isArray(response?.data) ? response.data : response?.data ? [response.data] : [];

      // Check if ANY product in the response matches all 3 criteria
      const hasPricing = responseData.some(item => {
        // Comparing mapped keys from your JSON example
        const isSpMatch = item.contractUniqueID === spNumber;
        const isDestinationMatch = item.destination === name;
        const isProductMatch = item.productGroup === timberProductUsedForWoodchip;

        return isSpMatch && isDestinationMatch && isProductMatch;
      });

      if (!hasPricing) {
        setPricingError('Selected SP, destination and product combination is not priced.');
        setIsPricingValid(false);
        return;
      }

      // If it makes it here, a match was found
      setPricingError('');
      setIsPricingValid(true);
    } catch (err) {
      console.error('Pricing validation failed', err);
      setPricingError('Unable to validate timber pricing. Please try again.');
      setIsPricingValid(false);
    }
  }, []);

  useEffect(() => {
    validateTimberPricing(formData);
  }, [validateTimberPricing, formData?.spNumber, formData?.name, formData?.timberProductUsedForWoodchip]);

  const fields = getInputArrayList(sectionName).filter(f => {
    if (sectionName === 'loadingDetails' && ['wH_Docket', 'permit', 'supplierDocket'].includes(f.name)) return false;
    return true;
  });
  const fieldNames = fields.map(f => f.name);

  const handleChange = (key, value) =>
    setFormData(prev => {
      const next = { ...prev, [key]: value };

      if (key === 'name') {
        const row = cwRows.find(r => r.destinationName?.trim() === value);
        if (row) {
          if (fieldNames.includes('customer')) next.customer = row.customerName?.trim() ?? '';
          if (fieldNames.includes('weightBridge')) next.weightBridge = row.weighbridgeName?.trim() ?? '';
        }
      }
      return next;
    });

  const handleClose = () => {
    console.log('Closing modal and resetting form data');
    handleEditClose();
    setFormData({});
  };

  useEffect(() => {
    console.log('EDIT WORKING =====');
    // Initialize formData when modal opens
    if (open && sectionData) {
      setFormData(sectionData);
    }
  }, [sectionData, open]);

  useEffect(() => {
    // Fetch users when needed
    if (sectionName === 'transfers' || sectionName === 'loadingDetails') {
      const fetchUsers = async () => {
        try {
          const res = await customAxios.get('/User/GetAllUsers', {
            headers: authHeader(),
          });
          setTransferUsers(Array.isArray(res?.data) ? res.data.map(u => ({ label: u.displayName, email: u.email })) : []);
        } catch (err) {
          console.error('Error fetching users:', err);
          setTransferUsers([]);
        }
      };
      fetchUsers();
    }
  }, [sectionName, open]);

  const handleSave = () => handleEditSave(sectionName, { ...formData });

  const isLoadingDetails = sectionName === 'loadingDetails';
  const isTransferDetails = sectionName === 'transfers';

  return (
    <Dialog
      maxWidth="xl"
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}>
      <DialogTitle style={{ paddingBottom: 6 }}>
        <Typography style={{ fontSize: '1.2rem', fontWeight: 600 }} component="div">
          Edit {camelCaseToLabel(sectionName)}
        </Typography>
      </DialogTitle>

      <DialogContent style={{ width: '72vw', paddingTop: 4 }}>
        {pricingError && (
          <Box mb={2}>
            <Alert severity="error">{pricingError}</Alert>
          </Box>
        )}
        <Grid container spacing={2}>
          {fields.map((inputProps, index) => {
            const isReadOnly = isLoadingDetails && ['docketId', 'whDocketNumber'].includes(inputProps.name);

            let dropdownData =
              inputProps.name === 'name'
                ? destinations
                :
                inputProps.name === 'isLastLoad' ?
                  [{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]
                  : inputProps.name === 'spNumber'
                    ? allWH
                    : inputProps.name === 'customer'
                      ? customers
                      : inputProps.name === 'driverName'
                        ? drivers
                        : inputProps.name === 'truck'
                          ? trucks
                          : inputProps.name === 'trailer'
                            ? trailers
                            : inputProps.name === 'loaderMachineOrTruck'
                              ? loadMachine
                              : inputProps.name === 'loadedBy'
                                ? loaders
                                : inputProps.name === 'weightBridge'
                                  ? weighbridges
                                  : inputProps.name === 'productType'
                                    ? ['Timber', 'Woodchip', 'Con. Haulage']
                                    : inputProps.name === 'timberProductUsedForWoodchip'
                                      ? product
                                      : inputProps.dropDownData || [];

            if (inputProps.name === 'driverUserName') dropdownData = transferUsers.map(u => u.email);
            if (isTransferDetails) {
              if (['transferToTruck', 'unloadingTruck'].includes(inputProps.name)) dropdownData = trucks;
              if (['transferToTrailer', 'unloadingTrailer'].includes(inputProps.name)) dropdownData = trailers;
              if (['transferToName', 'driverName'].includes(inputProps.name)) dropdownData = transferUsers.map(u => u.label);
              if (['transferToEmail', 'driverEmail'].includes(inputProps.name))
                dropdownData = transferUsers.map(u => u.email);
            }

            return (
              <React.Fragment key={`${inputProps.name}_${index}`}>
                {/* ── Group divider — only rendered when groupLabel is set ── */}
                {inputProps.groupLabel && (
                  <Grid item xs={12} style={{ paddingBottom: 0, marginTop: index === 0 ? 0 : 6 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          color: '#5476ff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          whiteSpace: 'nowrap',
                        }}>
                        {inputProps.groupLabel}
                      </Typography>
                      <Divider style={{ flex: 1 }} />
                    </Box>
                  </Grid>
                )}

                {/* ── Field ── */}
                <Grid item xs={12} sm={6} md={inputProps.width || 3}>
                  {inputProps.typeOfInp === 'select' && (
                    <>
                      {isTransferDetails && ['transferToName', 'driverName'].includes(inputProps.name) ? (
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={transferUsers.map(u => u.label)}
                          value={formData[inputProps.name] ?? ''}
                          onChange={(e, val) => {
                            handleChange(inputProps.name, val);
                            const match = transferUsers.find(u => u.label === val);
                            if (match) {
                              if (inputProps.name === 'transferToName') handleChange('transferToEmail', match.email);
                              if (inputProps.name === 'driverName') handleChange('driverEmail', match.email);
                            }
                          }}
                          onInputChange={(e, val) => handleChange(inputProps.name, val)}
                          renderInput={params => (
                            <TextField {...params} label={inputProps.label} variant="outlined" size="small" fullWidth />
                          )}
                        />
                      ) : isTransferDetails && ['transferToEmail', 'driverEmail'].includes(inputProps.name) ? (
                        <TextField
                          variant="outlined"
                          select
                          size="small"
                          fullWidth
                          label={inputProps.label}
                          value={formData[inputProps.name] ?? ''}
                          onChange={e => {
                            handleChange(inputProps.name, e.target.value);
                            const match = transferUsers.find(u => u.email === e.target.value);
                            if (match) {
                              if (inputProps.name === 'transferToEmail') handleChange('transferToName', match.label);
                              if (inputProps.name === 'driverEmail') handleChange('driverName', match.label);
                            }
                          }}>
                          {transferUsers.map((u, i) => (
                            <MenuItem key={`${u.email}_${i}`} value={u.email}>
                              {u.email}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : inputProps.isFreeText || inputProps.allowCustomInput ? (
                        /* freeSolo Autocomplete for fields that allow custom/other values */
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={
                            Array.isArray(dropdownData)
                              ? dropdownData.map(item => (typeof item === 'string' ? item : item.label || ''))
                              : []
                          }
                          value={formData[inputProps.name] ?? ''}
                          onChange={(e, val) => handleChange(inputProps.name, val ?? '')}
                          onInputChange={(e, val) => {
                            if (e) handleChange(inputProps.name, val);
                          }}
                          renderInput={params => (
                            <TextField {...params} label={inputProps.label} variant="outlined" size="small" fullWidth />
                          )}
                        />
                      ) : (
                        <TextField
                          variant="outlined"
                          select
                          size="small"
                          fullWidth
                          label={inputProps.label}
                          value={formData[inputProps.name] ?? ''}
                          onChange={e => handleChange(inputProps.name, e.target.value)}
                          InputProps={{ readOnly: isReadOnly }}>
                          {Array.isArray(dropdownData) &&
                            dropdownData.map((item, i) => {
                              const value = typeof item === 'string' ? item : item.label || '';
                              return (
                                <MenuItem key={`${value}_${i}`} value={value}>
                                  {value}
                                </MenuItem>
                              );
                            })}
                        </TextField>
                      )}
                    </>
                  )}

                  {inputProps.typeOfInp === 'input' && (
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      type={inputProps.textFieldType || 'text'}
                      label={inputProps.label}
                      value={
                        inputProps.textFieldType === 'datetime-local'
                          ? toLocalISO(formData[inputProps.name])
                          : formData[inputProps.name] ?? ''
                      }
                      onChange={e => handleChange(inputProps.name, e.target.value)}
                      multiline={inputProps.isTextArea}
                      rows={inputProps.isTextArea ? 3 : undefined}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: isReadOnly }}
                    />
                  )}
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions style={{ paddingTop: 8 }}>
        <Button variant="contained" color="primary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSectionModal;
